import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const AVG = 4.0;

const testimonials = [
  {
    name: "Marcus J.",
    area: "Catonsville, MD",
    stars: 5,
    quote:
      "Storm ripped through my block and Hero had a new roof on my house inside a week. I only paid my deductible — insurance covered the rest. Real professionals.",
  },
  {
    name: "Denise W.",
    area: "Baltimore County",
    stars: 4,
    quote:
      "My landlord handled the deductible, I just handled the coffee for the crew. They cleaned up every nail in the yard. Roof looks brand new.",
  },
  {
    name: "Trevor P.",
    area: "Ellicott City, MD",
    stars: 4,
    quote:
      "They walked me through the entire insurance conversation. Felt like they were on my side the whole time — no runaround.",
  },
  {
    name: "Lena R.",
    area: "Catonsville, MD",
    stars: 4,
    quote:
      "Neighbors keep asking who did my roof. That's how you know. Hero Exteriors earned every star.",
  },
];

const SIZE_CLS = { 4: "h-4 w-4", 5: "h-5 w-5", 6: "h-6 w-6", 7: "h-7 w-7" };

const Stars = ({ rating, size = 4 }) => {
  const rounded = Math.round(rating);
  const sz = SIZE_CLS[size] || SIZE_CLS[4];
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`${sz} ${i < rounded ? "fill-hero-red text-hero-red" : "text-white/20"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
};

export default function Reviews() {
  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-28 md:py-36 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-hero-red mb-4">— Vouchers</p>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
              Verified by the<br />neighbors themselves.
            </h2>
          </div>
          <p className="max-w-sm text-hero-silver">
            Every voucher below is from a real homeowner whose roof we made look fine.
          </p>
        </div>

        {/* Average rating hero card */}
        <motion.div
          data-testid="rating-average"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="bg-hero-surface border border-white/10 rounded-lg p-8 md:p-12 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-8"
        >
          <div className="flex items-center gap-6">
            <span className="font-display text-7xl md:text-8xl text-hero-red leading-none">{AVG.toFixed(1)}</span>
            <div>
              <Stars rating={AVG} size={6} />
              <p className="mt-2 text-white font-semibold uppercase tracking-wide text-sm">
                Average Star Rating
              </p>
              <p className="text-hero-silver text-sm">from Maryland homeowners</p>
            </div>
          </div>
          <div className="md:text-right">
            <p className="font-display uppercase text-2xl md:text-3xl leading-tight max-w-sm">
              We average 4-star reviews on every job we finish.
            </p>
          </div>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <motion.article
              key={t.name}
              data-testid={`voucher-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-hero-void border border-white/10 rounded-lg p-8 hover:border-hero-red/60 transition-colors duration-300"
            >
              <Quote className="absolute top-6 right-6 h-8 w-8 text-hero-red/20 group-hover:text-hero-red/50 transition-colors duration-300" strokeWidth={1.5} />
              <Stars rating={t.stars} size={5} />
              <p className="mt-5 text-white text-lg leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <span className="font-display uppercase text-lg tracking-wide">{t.name}</span>
                <span className="text-hero-silver text-sm">{t.area}</span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
